/**
 * ReDoS-safe helpers for matching untrusted strings against fixed literal patterns (CWE-185).
 *
 * Always cap input length before calling RegExp methods — catastrophic backtracking
 * is bounded when input cannot grow without limit.
 */

/** Default max length for regex matching on user-supplied or server-returned text. */
export const SAFE_REGEX_INPUT_MAX_LENGTH = 8192

export function safeRegexTest(
  pattern: RegExp,
  input: string,
  maxLength: number = SAFE_REGEX_INPUT_MAX_LENGTH,
): boolean {
  if (input.length > maxLength) return false
  return pattern.test(input)
}

export function safeRegexExec(
  pattern: RegExp,
  input: string,
  maxLength: number = SAFE_REGEX_INPUT_MAX_LENGTH,
): RegExpExecArray | null {
  if (input.length > maxLength) return null
  return pattern.exec(input)
}

export function safeRegexMatch(
  pattern: RegExp,
  input: string,
  maxLength: number = SAFE_REGEX_INPUT_MAX_LENGTH,
): RegExpMatchArray | null {
  if (input.length > maxLength) return null
  return input.match(pattern)
}

/** Letters, digits, spaces, hyphens, underscores — no RegExp on untrusted input. Optionally dots. */
export function isResourceNameChars(
  value: string,
  allowDots: boolean = false,
  allowBrackets: boolean = true,
  allowColons: boolean = true,
  allowAmpersand: boolean = false,
): boolean {
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i)
    const isLetter = (c >= 65 && c <= 90) || (c >= 97 && c <= 122)
    const isDigit = c >= 48 && c <= 57
    const isSpace = c === 32
    const isHyphen = c === 45
    const isUnderscore = c === 95
    const isDot = c === 46
    const isBracket = c === 40 || c === 41 || c === 91 || c === 93 || c === 123 || c === 125 // (), [], {}
    const isColon = c === 58 // :
    const isAmpersand = c === 38 // &
    
    const allowed =
      isLetter ||
      isDigit ||
      isSpace ||
      isHyphen ||
      isUnderscore ||
      (allowDots && isDot) ||
      (allowBrackets && isBracket) ||
      (allowColons && isColon) ||
      (allowAmpersand && isAmpersand)
    if (!allowed) {
      return false
    }
  }
  return true
}

/** Simple email shape check without applying a regex to untrusted input. */
export function isLikelyEmail(value: string, maxLength: number = 254): boolean {
  if (!value || value.length > maxLength) return false
  const at = value.indexOf('@')
  if (at <= 0 || at !== value.lastIndexOf('@')) return false
  const local = value.slice(0, at)
  const domain = value.slice(at + 1)
  if (!local || !domain || local.includes(' ') || domain.includes(' ')) return false
  const dot = domain.lastIndexOf('.')
  return dot > 0 && dot < domain.length - 1
}

/** UUID v4-style hex check without RegExp. */
export function isUuidFormat(value: string): boolean {
  const v = value.trim().toLowerCase()
  if (v.length !== 36) return false
  const parts = v.split('-')
  if (parts.length !== 5) return false
  const lens = [8, 4, 4, 4, 12]
  for (let i = 0; i < 5; i++) {
    if (parts[i].length !== lens[i]) return false
    for (const c of parts[i]) {
      const code = c.charCodeAt(0)
      const isDigit = code >= 48 && code <= 57
      const isHex = code >= 97 && code <= 102
      if (!isDigit && !isHex) return false
    }
  }
  return true
}

/** True when every character is ASCII digit. */
export function isAsciiDigits(value: string): boolean {
  if (!value) return false
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i)
    if (c < 48 || c > 57) return false
  }
  return true
}

/** True when every character is ASCII letter or digit. */
export function isAsciiAlphanumeric(value: string): boolean {
  if (!value) return false
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i)
    const isLetter = (c >= 65 && c <= 90) || (c >= 97 && c <= 122)
    const isDigit = c >= 48 && c <= 57
    if (!isLetter && !isDigit) return false
  }
  return true
}

/** True when every character is ASCII letter, digit, or underscore. */
export function isAsciiIdentifier(value: string): boolean {
  if (!value) return false
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i)
    const isLetter = (c >= 65 && c <= 90) || (c >= 97 && c <= 122)
    const isDigit = c >= 48 && c <= 57
    const isUnderscore = c === 95
    if (!isLetter && !isDigit && !isUnderscore) return false
  }
  return true
}

/** Strip characters that are not allowed in ASCII identifiers (letters, digits, underscores). */
export function filterAsciiIdentifierChars(value: string): string {
  let result = ''
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i)
    const isLetter = (c >= 65 && c <= 90) || (c >= 97 && c <= 122)
    const isDigit = c >= 48 && c <= 57
    const isUnderscore = c === 95
    if (isLetter || isDigit || isUnderscore) {
      result += value[i]
    }
  }
  return result
}

/** True when the value contains at least one ASCII letter or digit. */
export function hasAsciiLetterOrDigit(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i)
    const isLetter = (c >= 65 && c <= 90) || (c >= 97 && c <= 122)
    const isDigit = c >= 48 && c <= 57
    if (isLetter || isDigit) return true
  }
  return false
}
